
import { LinkedUser } from '@/hooks/use-broker';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, UserIcon, CalendarIcon } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';

interface LinkedUsersListProps {
  linkedUsers: LinkedUser[];
  isLoading: boolean;
}

export const LinkedUsersList = ({ linkedUsers, isLoading }: LinkedUsersListProps) => {
  if (isLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  if (linkedUsers.length === 0) {
    return (
      <Card>
        <CardContent className="flex flex-col items-center justify-center py-12">
          <UserIcon className="h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-xl font-semibold mb-2">No Linked Clients Yet</h3>
          <p className="text-muted-foreground text-center max-w-md">
            When clients use your referral code to link their account, they'll appear here.
            Share your referral code to start building your client list.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4">
      {linkedUsers.map((link) => (
        <Card key={link.id} className="hover:shadow-md transition-shadow">
          <CardContent className="p-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-lg">
                  {link.profile.first_name} {link.profile.last_name}
                </h3>
                <p className="text-sm text-muted-foreground">{link.profile.email}</p>
                <div className="flex items-center mt-1 text-xs text-muted-foreground">
                  <CalendarIcon className="h-3 w-3 mr-1" />
                  <span>Linked {formatDistanceToNow(new Date(link.created_at), { addSuffix: true })}</span>
                </div>
              </div>
              
              <div className="text-right">
                <div className="flex flex-col">
                  <span className="text-sm font-medium">
                    {link.applications ? link.applications.length : 0} Applications
                  </span>
                  {link.applications && link.applications.length > 0 && (
                    <span className="text-xs text-muted-foreground">
                      Latest: {formatDistanceToNow(new Date(link.applications[0].created_at), { addSuffix: true })}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
